import React, { useEffect, useRef, useState } from "react";

export const AttachmentPreview = ({
    file,
    disabled,
    onRemove
}) => {
    const [previewUrl, setPreviewUrl] = useState("");

    const isPdf =
        file.type === "application/pdf" ||
        /\.pdf$/i.test(file.name);

    useEffect(() => {
        if (!(file instanceof Blob)) return;

        const url = URL.createObjectURL(file);
        setPreviewUrl(url);

        return () => URL.revokeObjectURL(url);
    }, [file]);

    return (
        <div
            className="d-flex align-items-center gap-2 p-2"
            style={{
                border: "1px solid #dee2e6",
                borderRadius: "6px",
                background: "#f8f9fa",
                minWidth: 0
            }}
        >
            {previewUrl && (
                <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Preview ${file.name}`}
                    aria-label={`Preview ${file.name}`}
                    style={{ flexShrink: 0 }}
                >
                    {isPdf ? (
                        <span
                            className="d-flex align-items-center justify-content-center"
                            style={{
                                width: "64px",
                                height: "52px",
                                background: "#fff",
                                border: "1px solid #dee2e6",
                                borderRadius: "4px",
                                color: "#dc3545",
                                fontSize: "12px",
                                fontWeight: 600
                            }}
                        >
                            PDF
                        </span>
                    ) : (
                        <img
                            src={previewUrl}
                            alt={file.name}
                            style={{
                                display: "block",
                                width: "64px",
                                height: "52px",
                                objectFit: "contain",
                                border: "1px solid #dee2e6",
                                borderRadius: "4px",
                                background: "#fff"
                            }}
                        />
                    )}
                </a>
            )}

            <div style={{ flex: "1 1 0", minWidth: 0 }}>
                <div
                    style={{
                        fontSize: "11px",
                        color: "#6c757d",
                        overflowWrap: "anywhere"
                    }}
                >
                    {file.name}
                </div>

                <div className="text-muted" style={{ fontSize: "10px" }}>
                    {Math.ceil(file.size / 1024)} KB
                    {previewUrl && (
                        <a
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ms-2"
                        >
                            Preview
                        </a>
                    )}
                </div>
            </div>

            <button
                type="button"
                className="btn btn-outline-danger flex-shrink-0"
                disabled={disabled}
                onClick={onRemove}
                title="Remove attachment"
                aria-label={`Remove ${file.name}`}
                style={{
                    padding: "2px 8px",
                    minHeight: "28px",
                    fontSize: "16px",
                    lineHeight: 1
                }}
            >
                ×
            </button>
        </div>
    );
};

const CashEntryAttachments = ({
    inputId = "cash-entry-attachments",
    inputRef,
    attachments = [],
    disabled = false,
    processing = false,
    onChange,
    onRemoveRow,
    canRemoveRow = false,
    showPreviews = true
}) => {
    const localInputRef = useRef(null);
    const fileInputRef = inputRef || localInputRef;
    const controlsDisabled = disabled || processing;

    return (
        <div>
            <label htmlFor={inputId} className="form-label">
                Bills / Attachments
            </label>

            <div className="d-flex align-items-center gap-2">
                <input
                    ref={fileInputRef}
                    id={inputId}
                    type="file"
                    className="form-control"
                    style={{ minWidth: 0, flex: "1 1 0" }}
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    multiple
                    disabled={controlsDisabled}
                    onChange={(event) => {
                        const selectedFiles = Array.from(
                            event.target.files || []
                        );

                        event.target.value = "";

                        if (selectedFiles.length) {
                            onChange?.([...attachments, ...selectedFiles]);
                        }
                    }}
                />

                {canRemoveRow && (
                    <button
                        type="button"
                        className="btn btn-outline-danger flex-shrink-0"
                        disabled={controlsDisabled}
                        onClick={onRemoveRow}
                        title="Delete this entry row"
                        aria-label="Delete entry row"
                        style={{
                            padding: "6px 10px",
                            fontSize: "11px",
                            whiteSpace: "nowrap"
                        }}
                    >
                        Delete Row
                    </button>
                )}
            </div>

            <div className="small text-muted mt-1">
                Multiple images and PDFs allowed. Maximum 5 MB per file.
            </div>

            {processing && (
                <div
                    className="small text-primary mt-1"
                    role="status"
                    aria-live="polite"
                >
                    <span
                        className="spinner-border spinner-border-sm me-1"
                        aria-hidden="true"
                    />
                    Compressing images...
                </div>
            )}

            {showPreviews && attachments.length > 0 && (
                <div className="d-grid gap-2 mt-2">
                    {attachments.map((file, index) => (
                        <AttachmentPreview
                            key={`${file.name}-${file.lastModified}-${index}`}
                            file={file}
                            disabled={controlsDisabled}
                            onRemove={() =>
                                onChange?.(
                                    attachments.filter(
                                        (_, fileIndex) => fileIndex !== index
                                    )
                                )
                            }
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default CashEntryAttachments;